import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Input } from 'us-react-components/dist/Input/Input';
import { ReferralOffer, ReferralOfferService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { DisplayUtil } from '../../utils';
import './Referral.scss';

export const Referral = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [offer, setOffer] = useState<ReferralOffer | null>(null);

  useEffect(() => {
    document.title = 'UrbanSitter - Referral';
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    if (pathId && Number(pathId) !== user.id) {
      navigate(`/parent/${user.id}/referral`, { replace: true });
      return;
    }

    const load = async (): Promise<void> => {
      try {
        const resp = await ReferralOfferService.create(user.id, { platform: DisplayUtil.getPlatform() });
        setOffer(resp.data?.data ?? null);
      } catch (err) {
        console.warn('[Referral] load failed', err);
      }
    };
    void load();
  }, [user?.id, pathId, navigate]);

  const handleCopy = async (): Promise<void> => {
    if (!offer?.branchLink) return;
    try {
      await navigator.clipboard.writeText(offer.branchLink);
      toast.success(t('notifications.referral.linkCopied'));
    } catch {
      toast.error(t('notifications.referral.copyFailed'));
    }
  };

  if (!offer) {
    return <div className='container py-6' />;
  }

  return (
    <section
      id='referral-page'
      style={{
        backgroundImage: 'url(https://assets.urbansitter.com/static/img/give_get.webp)',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className='container'>
        <div className='flex justify-center'>
          <div className='referral-content-card w-full sm:w-8/12 bg-white p-5 sm:p-10'>
            {offer.pageTitle && (
              <h2 id='share-page-title' className='text-2xl font-semibold mb-2 text-center'>
                {offer.pageTitle}
              </h2>
            )}
            {offer.pageContent && (
              <h5 id='share-page-content' className='text-base mb-6 text-center'>
                {offer.pageContent}
              </h5>
            )}
            <h6 className='text-sm font-semibold mt-6 mb-1'>
              {t('pages.referral.yourLinkLabel')}
            </h6>
            <div className='flex flex-col sm:flex-row gap-3'>
              <Input
                id='share-link-url'
                className='flex-1'
                type='text'
                readOnly
                value={offer.branchLink ?? ''}
                aria-label={t('pages.referral.yourLinkLabel')}
                onClick={(e) => (e.target as HTMLInputElement).select()}
                onChange={() => { /* readOnly */ }}
              />
              <Button
                id='copy-referral-link'
                buttonType={ButtonType.primary}
                onClick={handleCopy}
                disabled={!offer.branchLink}
              >
                {t('pages.referral.copyButton')}
              </Button>
            </div>
            <div className='mt-4'>
              <a
                href='/referral-terms'
                target='_blank'
                rel='noopener noreferrer'
                className='font-semibold underline'
              >
                {t('pages.referral.termsLink')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});
